import React, { useState } from 'react';
import { HelpCircle, X } from 'lucide-react';

/**
 * QuickTip - Contextual tooltip component
 * Usage: <QuickTip tip="Your helpful tip here" />
 */
export const QuickTip = ({ tip, position = 'top', className = '' }) => {
  const [show, setShow] = useState(false);
  
  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  return (
    <span className={`relative inline-flex ${className}`}>
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
        className="text-slate-400 hover:text-pink-500 transition-colors focus:outline-none"
        data-testid="quick-tip-trigger"
      >
        <HelpCircle className="w-4 h-4" />
      </button>
      {show && (
        <div 
          className={`absolute z-50 ${positionClasses[position]} animate-fade-in`}
          data-testid="quick-tip-content"
        >
          <div className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 max-w-[220px] shadow-lg">
            <div className="relative">
              {tip}
            </div>
          </div>
        </div>
      )}
    </span>
  );
};

/**
 * QuickTipCard - Dismissible tip card for onboarding/help
 */
export const QuickTipCard = ({ title, tip, onDismiss, icon: Icon = HelpCircle }) => {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 relative" data-testid="quick-tip-card">
      <button 
        onClick={onDismiss}
        className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-blue-600" />
        </div>
        <div>
          {title && <h4 className="font-semibold text-blue-800 text-sm mb-1">{title}</h4>}
          <p className="text-blue-700 text-xs leading-relaxed">{tip}</p>
        </div>
      </div>
    </div>
  );
};

/**
 * StatusTooltip - Tooltip for booking status badges
 */
export const StatusTooltip = ({ status, children }) => {
  const statusTips = {
    provisional: 'Awaiting payment. Guest has received a payment link via email.',
    waitlist: 'Overlaps with another provisional booking. First to pay gets confirmed.',
    confirmed: 'Payment received. Booking is guaranteed.',
    paid: 'Payment received. Booking is guaranteed.',
    'checked-in': 'Guest has arrived and is currently staying.',
    'checked-out': 'Guest has departed. Property ready for turnover.',
    cancelled: 'Booking was cancelled by guest, host, or system.',
  };

  return (
    <span className="relative group">
      {children}
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50">
        <span className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg block">
          {statusTips[status] || 'Booking status'}
        </span>
      </span>
    </span>
  );
};

export default QuickTip;
